/**
 * Bounded rendering for large OCS results (#663, payload half).
 *
 * Two tools return results that do not fit an MCP `tools/call` response:
 *
 *   get_tariff                    ~2.0 MB compact / 3.2 MB pretty, 2,657 rules — never returns
 *   list_detailed_location_zones   325 KB — the largest result PROVEN to work
 *
 * The measured working ceiling sits above 325 KB and below 2 MB. Everything
 * here is sized to land comfortably *below* the 325 KB proven-good mark rather
 * than anywhere inside that untested gap.
 *
 * Three independent mechanisms, in the order that matters:
 *
 *   1. PROJECTION (biggest win). A tariff rule is ~754 B compact, and almost
 *      all of that is the nested `operator` (with `mccMncs[]` / `tadigs[]`),
 *      `currency` and `sponsor` objects repeated on every single row — not the
 *      rates. Projecting to short-keyed rate rows takes a row to ~87 B.
 *   2. FILTERING, applied server-side. `getCustomerTariff` takes a bare integer
 *      and the REST route demonstrably ignores query params (`?country=` and
 *      `?trafficType=` return byte-identical bodies), so the filter cannot be
 *      pushed down to OCS.
 *   3. CAPPING with an explicit, never-silent truncation note that names the
 *      exact params which would narrow the request.
 */

// ---------------------------------------------------------------------------
// Caps
// ---------------------------------------------------------------------------

/**
 * Projected tariff rows cost ~87 B each (compact JSON, measured on the live
 * 2,657-rule reseller-1170 table). 3,000 rows is therefore a hard ceiling of
 * roughly 261 KB — still below the 325 KB result size proven to traverse the
 * transport — while sitting above this tenant's 2,657 rules, so a real caller
 * gets the complete table today and a pathological tenant degrades to a
 * documented, non-silent sample.
 */
export const TARIFF_MAX_RULES = 3000;

/**
 * The full untouched rule shape is ~754 B compact. `verbose` therefore gets a
 * far smaller row budget: 250 rows ≈ 190 KB, same headroom, same guarantee.
 */
export const TARIFF_VERBOSE_MAX_RULES = 250;

/**
 * Zones are cheap; their embedded operator lists are not (755 operator entries
 * on this tenant). Bound the operator entries, which is where the size lives.
 */
export const ZONES_MAX_ZONES = 100;
export const ZONES_MAX_OPERATORS = 2000;
export const ZONES_VERBOSE_MAX_OPERATORS = 400;

export type TrafficType = "data" | "voice" | "sms";

// ---------------------------------------------------------------------------
// Raw OCS shapes (only the fields we read)
// ---------------------------------------------------------------------------

interface RawOperator {
  networkId?: number;
  countryName?: string;
  countryIso2?: string;
  operatorName?: string;
  [k: string]: unknown;
}

interface RawTariffRule {
  operator?: RawOperator;
  currency?: { currencycode?: string; [k: string]: unknown };
  datarate?: number;
  mocallrate?: number;
  mtcallrate?: number;
  mosmsrate?: number;
  mtsmsrate?: number;
  active?: boolean;
  [k: string]: unknown;
}

interface RawZone {
  zoneId?: number;
  zoneName?: string;
  operators?: RawOperator[];
  [k: string]: unknown;
}

/** `getCustomerTariff` answers `{ listTariffRule: { rule: [...] } }`; the client already unwraps one level. */
export function extractTariffRules(raw: unknown): RawTariffRule[] {
  if (Array.isArray(raw)) return raw as RawTariffRule[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of ["rule", "listTariffRule", "rules"]) {
      const v = obj[key];
      if (Array.isArray(v)) return v as RawTariffRule[];
      if (v && typeof v === "object" && Array.isArray((v as Record<string, unknown>).rule)) {
        return (v as { rule: RawTariffRule[] }).rule;
      }
    }
  }
  return [];
}

export function extractZones(raw: unknown): RawZone[] {
  if (Array.isArray(raw)) return raw as RawZone[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    for (const key of ["listDetailedLocationZone", "locationZone", "zone", "zones"]) {
      const v = obj[key];
      if (Array.isArray(v)) return v as RawZone[];
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * Country match is case-insensitive and accepts either the ISO-3166 alpha-2
 * code ("nl", "NL") or the full country name ("Netherlands"). Names are
 * compared exactly (case-insensitively) after trimming, so "United States"
 * cannot accidentally sweep in "United States Minor Outlying Islands" via a
 * substring match.
 */
export function matchesCountry(op: RawOperator | undefined, country: string): boolean {
  const want = country.trim().toLowerCase();
  if (!want) return true;
  const iso = (op?.countryIso2 ?? "").trim().toLowerCase();
  const name = (op?.countryName ?? "").trim().toLowerCase();
  return iso === want || name === want;
}

/**
 * trafficType rule (documented, deliberately conservative):
 *
 * OCS has no traffic-type field — every rule carries all five rate columns. So
 * "give me the data rules" is answered as *rules where the relevant rate is
 * meaningful*, i.e. the rate is a number greater than zero:
 *
 *   data  -> datarate > 0
 *   voice -> mocallrate > 0 OR mtcallrate > 0
 *   sms   -> mosmsrate > 0 OR mtsmsrate > 0
 *
 * A rule priced at 0 for a traffic type is not a tariff for that traffic type;
 * it is the absence of one (typically a bundled/unbilled leg such as mtcall).
 * No field is invented.
 */
export function matchesTrafficType(rule: RawTariffRule, trafficType: TrafficType): boolean {
  const gt0 = (v: unknown): boolean => typeof v === "number" && v > 0;
  switch (trafficType) {
    case "data":
      return gt0(rule.datarate);
    case "voice":
      return gt0(rule.mocallrate) || gt0(rule.mtcallrate);
    case "sms":
      return gt0(rule.mosmsrate) || gt0(rule.mtsmsrate);
  }
}

/** Rate columns kept for a given trafficType. Unset = all five. */
const RATE_COLUMNS: Record<TrafficType, ReadonlyArray<[keyof RawTariffRule, string]>> = {
  data: [["datarate", "data"]],
  voice: [
    ["mocallrate", "moCall"],
    ["mtcallrate", "mtCall"],
  ],
  sms: [
    ["mosmsrate", "moSms"],
    ["mtsmsrate", "mtSms"],
  ],
};

const ALL_RATE_COLUMNS: ReadonlyArray<[keyof RawTariffRule, string]> = [
  ["datarate", "data"],
  ["mocallrate", "moCall"],
  ["mtcallrate", "mtCall"],
  ["mosmsrate", "moSms"],
  ["mtsmsrate", "mtSms"],
];

// ---------------------------------------------------------------------------
// Tariff projection
// ---------------------------------------------------------------------------

export interface TariffProjectedRow {
  /** ISO-3166 alpha-2 country code, lower-case as OCS emits it. */
  iso?: string;
  /** Full country name — only under `verbose`-adjacent callers; see below. */
  country?: string;
  /** Operator / network name. */
  op?: string;
  data?: number;
  moCall?: number;
  mtCall?: number;
  moSms?: number;
  mtSms?: number;
  /** Present and `true` only when the rule is active; absent means inactive. */
  active?: true;
}

export interface TariffBoundedResult {
  resellerId?: number;
  /** Rules returned by OCS before any filtering. */
  totalRules: number;
  /** Rules remaining after `country` / `trafficType` filtering. */
  matchedRules: number;
  /** Rules actually present in `rules` (≤ the cap). */
  returnedRules: number;
  truncated: boolean;
  /** Hoisted off every row when the whole table shares one currency. */
  currency?: string;
  filters?: { country?: string; trafficType?: TrafficType };
  projection: "compact" | "verbose";
  /** Field legend so an LLM caller never has to guess a short key. */
  fields?: Record<string, string>;
  note?: string;
  rules: unknown[];
}

const FIELD_LEGEND: Record<string, string> = {
  iso: "ISO-3166 alpha-2 country code",
  op: "operator / network name",
  data: "data rate per MB",
  moCall: "mobile-originated call rate per minute",
  mtCall: "mobile-terminated call rate per minute",
  moSms: "mobile-originated SMS rate",
  mtSms: "mobile-terminated SMS rate",
  active: "present only when the rule is active; absent means inactive",
};

export function truncationNote(
  kind: "tariff" | "zones",
  returned: number,
  matched: number,
): string {
  if (kind === "tariff") {
    return (
      `TRUNCATED: showing ${returned.toLocaleString("en-US")} of ${matched.toLocaleString("en-US")} matching tariff rules ` +
      `(cap ${TARIFF_MAX_RULES.toLocaleString("en-US")} rows, imposed so the result fits the MCP transport — see issue #663). ` +
      `Narrow the request to see the rest: pass 'country' (ISO2 such as "nl" or a full name such as "Netherlands") ` +
      `and/or 'trafficType' ("data" | "voice" | "sms"). These filters are applied server-side to the complete OCS table, ` +
      `so a filtered call sees every matching rule, not just the rules in this sample.`
    );
  }
  return (
    `TRUNCATED: showing ${returned.toLocaleString("en-US")} of ${matched.toLocaleString("en-US")} operator entries ` +
    `across the returned zones (cap ${ZONES_MAX_OPERATORS.toLocaleString("en-US")} entries, imposed so the result fits the MCP transport — see issue #663). ` +
    `Pass 'locationZoneId' to retrieve a single zone in full.`
  );
}

export interface BoundTariffOptions {
  resellerId?: number;
  country?: string;
  trafficType?: TrafficType;
  verbose?: boolean;
  /** Test seam; defaults to the module caps. */
  maxRules?: number;
}

export function boundTariffResult(
  raw: unknown,
  opts: BoundTariffOptions = {},
): TariffBoundedResult {
  const all = extractTariffRules(raw);
  const { country, trafficType, verbose = false, resellerId } = opts;

  const matched = all.filter(
    (r) =>
      (country === undefined || matchesCountry(r.operator, country)) &&
      (trafficType === undefined || matchesTrafficType(r, trafficType)),
  );

  const cap =
    opts.maxRules ?? (verbose ? TARIFF_VERBOSE_MAX_RULES : TARIFF_MAX_RULES);
  const kept = matched.slice(0, cap);
  const truncated = kept.length < matched.length;

  const result: TariffBoundedResult = {
    resellerId,
    totalRules: all.length,
    matchedRules: matched.length,
    returnedRules: kept.length,
    truncated,
    projection: verbose ? "verbose" : "compact",
    rules: [],
  };

  if (country !== undefined || trafficType !== undefined) {
    result.filters = {};
    if (country !== undefined) result.filters.country = country;
    if (trafficType !== undefined) result.filters.trafficType = trafficType;
  }

  if (verbose) {
    result.rules = kept;
    if (truncated) result.note = truncationNote("tariff", kept.length, matched.length);
    return result;
  }

  // Hoist the currency when the whole matched set shares one — it is a constant
  // string repeated on every row otherwise.
  const currencies = new Set(
    kept.map((r) => r.currency?.currencycode).filter((c): c is string => typeof c === "string"),
  );
  const hoistCurrency = currencies.size === 1;
  if (hoistCurrency) result.currency = [...currencies][0];

  const columns = trafficType ? RATE_COLUMNS[trafficType] : ALL_RATE_COLUMNS;

  result.rules = kept.map((r) => {
    const op = r.operator ?? {};
    const row: TariffProjectedRow & Record<string, unknown> = {};
    if (op.countryIso2 !== undefined) row.iso = op.countryIso2;
    if (op.operatorName !== undefined) row.op = op.operatorName;
    for (const [src, dst] of columns) {
      const v = r[src];
      if (typeof v === "number" && v !== 0) row[dst] = v;
    }
    if (!hoistCurrency && typeof r.currency?.currencycode === "string") {
      row.cur = r.currency.currencycode;
    }
    if (r.active === true) row.active = true;
    return row;
  });

  const legend: Record<string, string> = { ...FIELD_LEGEND };
  if (trafficType) {
    for (const key of ["data", "moCall", "mtCall", "moSms", "mtSms"]) {
      if (!columns.some(([, dst]) => dst === key)) delete legend[key];
    }
  }
  if (!hoistCurrency) legend.cur = "ISO-4217 currency code";
  result.fields = legend;

  const notes: string[] = [];
  if (truncated) notes.push(truncationNote("tariff", kept.length, matched.length));
  notes.push(
    "Projected result: nested operator detail (mccMncs, tadigs, continent, countryCode, utcOffset), " +
      "the sponsor object, plan ids (roamingplanruleid, roamingplanid, sponsoridx) and the validity/discount " +
      "flags (startdate, enddate, isDiscounted, hidden, dailyCap) are omitted. " +
      "Pass verbose=true for the full untouched rule shape (capped at " +
      `${TARIFF_VERBOSE_MAX_RULES} rows — use 'country'/'trafficType' with it).`,
  );
  result.note = notes.join(" ");

  return result;
}

// ---------------------------------------------------------------------------
// Location zones
// ---------------------------------------------------------------------------

export interface ZonesBoundedResult {
  resellerId?: number;
  totalZones: number;
  returnedZones: number;
  totalOperators: number;
  returnedOperators: number;
  truncated: boolean;
  projection: "compact" | "verbose";
  fields?: Record<string, string>;
  note?: string;
  zones: unknown[];
}

const ZONE_FIELD_LEGEND: Record<string, string> = {
  zoneId: "location zone id — pass to package-template tools",
  zoneName: "human-readable zone name",
  operatorCount: "total operator entries in this zone upstream",
  operators: "projected operator entries: net (networkId), iso (country ISO2), op (operator name)",
};

export interface BoundZonesOptions {
  resellerId?: number;
  verbose?: boolean;
  maxZones?: number;
  maxOperators?: number;
}

export function boundZonesResult(
  raw: unknown,
  opts: BoundZonesOptions = {},
): ZonesBoundedResult {
  const all = extractZones(raw);
  const { verbose = false, resellerId } = opts;

  const maxZones = opts.maxZones ?? ZONES_MAX_ZONES;
  const maxOperators =
    opts.maxOperators ?? (verbose ? ZONES_VERBOSE_MAX_OPERATORS : ZONES_MAX_OPERATORS);

  const totalOperators = all.reduce((n, z) => n + (z.operators?.length ?? 0), 0);
  const keptZones = all.slice(0, maxZones);

  let budget = maxOperators;
  let returnedOperators = 0;
  const zones = keptZones.map((z) => {
    const ops = z.operators ?? [];
    const take = Math.max(0, Math.min(ops.length, budget));
    budget -= take;
    returnedOperators += take;
    const sliced = ops.slice(0, take);
    if (verbose) {
      return { ...z, operators: sliced, operatorCount: ops.length };
    }
    return {
      zoneId: z.zoneId,
      zoneName: z.zoneName,
      operatorCount: ops.length,
      operators: sliced.map((o) => ({
        net: o.networkId,
        iso: o.countryIso2,
        op: o.operatorName,
      })),
    };
  });

  const truncated = keptZones.length < all.length || returnedOperators < totalOperators;

  const result: ZonesBoundedResult = {
    resellerId,
    totalZones: all.length,
    returnedZones: keptZones.length,
    totalOperators,
    returnedOperators,
    truncated,
    projection: verbose ? "verbose" : "compact",
    zones,
  };

  const notes: string[] = [];
  if (truncated) notes.push(truncationNote("zones", returnedOperators, totalOperators));
  if (!verbose) {
    result.fields = ZONE_FIELD_LEGEND;
    notes.push(
      "Projected result: per-operator mccMncs, tadigs, continent, countryCode, countryName and " +
        "utcOffset are omitted, as is the repeated reseller object. Pass verbose=true for the full " +
        `untouched zone shape (capped at ${ZONES_VERBOSE_MAX_OPERATORS} operator entries).`,
    );
  }
  if (notes.length) result.note = notes.join(" ");

  return result;
}
